import log from 'loglevel'

import { emitter } from '@/helpers'
import i18n from '@/localization'

export class ErrorHandler {
  static isError(error: unknown): error is Error {
    return (
      error instanceof Error ||
      (typeof error === 'object' && error !== null && 'message' in error)
    )
  }

  // Show a user-facing error via the global emitter and log it
  static process(error: unknown, message = ''): void {
    const appErr = error instanceof AppError ? error : undefined
    const msg =
      message ||
      appErr?.userMessage ||
      (ErrorHandler.isError(error) ? error.message : '') ||
      i18n.t('errors.default')

    const title = appErr?.title || i18n.t('errors.default-title-error')

    emitter.emit('error', { title, message: msg })

    ErrorHandler.processWithoutFeedback(appErr?.originalError ?? error)
  }

  // Only log, without user feedback
  static processWithoutFeedback(error: unknown): void {
    // Keep logging simple and side-effect free
    try {
      if (ErrorHandler.isError(error)) {
        log.error(error)
      } else {
        log.error('Unknown error', error)
      }
    } catch {
      // no-op: never throw from error handler
    }
  }
}

export class AppError extends Error {
  title?: string
  userMessage?: string
  originalError?: unknown
  context?: string

  constructor(options: {
    title?: string
    userMessage?: string
    context?: string
    originalError?: unknown
    message?: string
  }) {
    super(options.message || options.userMessage || options.title || 'Error')
    this.name = 'AppError'
    this.title = options.title
    this.userMessage = options.userMessage
    this.originalError = options.originalError
    this.context = options.context
  }
}

export function extractMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  try {
    if (typeof error === 'object' && error !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = error as Record<string, any>
      if (typeof obj.message === 'string') return obj.message
      if (typeof obj.error === 'string') return obj.error
      const response = obj.response
      const data = response?.data
      if (typeof data?.message === 'string') return data.message
    }
  } catch {
    // ignore
  }
  return ''
}
